import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { api } from '../utils/api';

const AutomataContext = createContext(null);

export const useAutomata = () => {
  const context = useContext(AutomataContext);
  if (!context) {
    throw new Error('useAutomata must be used within AutomataProvider');
  }
  return context;
};

const generateStateId = () => `state_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateTransitionId = () => `trans_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const AutomataProvider = ({ children }) => {
  const { user } = useAuth();
  const isGuest = !user;
  const [automata, setAutomata] = useState([]);
  const [currentAutomaton, setCurrentAutomaton] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const autoSaveTimeoutRef = useRef(null);

  // Auto-save debounce (only for authenticated users)
  const scheduleAutoSave = useCallback((automatonData) => {
    if (isGuest) return; // No auto-save for guests
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!automatonData?.automaton_id) return;
      
      setSaving(true);
      try {
        const payload = {
            name: automatonData.name,
            type: automatonData.type,
            states: automatonData.states,
            alphabet: automatonData.alphabet,
            transitions: automatonData.transitions,
            language_description: automatonData.language_description,
            test_accept_strings: automatonData.test_accept_strings,
            test_reject_strings: automatonData.test_reject_strings,
        };
        if (automatonData.type === 'PDA') {
          payload.initial_stack_symbol = automatonData.initial_stack_symbol;
          payload.acceptance_mode = automatonData.acceptance_mode;
        }
        if (automatonData.type === 'TM') {
          payload.blank_symbol = automatonData.blank_symbol;
          payload.step_limit = automatonData.step_limit;
        }
        await api.put(`/automata/${automatonData.automaton_id}`, payload);
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setSaving(false);
      }
    }, 1000);
  }, [isGuest]);

  // Fetch user's automata
  const fetchAutomata = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { ok, data } = await api.get('/automata');
      
      if (ok) {
        setAutomata(data);

        // Auto-select imported guest automaton if pending
        const pendingId = localStorage.getItem('pendingSelectAutomaton');
        if (pendingId) {
          localStorage.removeItem('pendingSelectAutomaton');
          const match = data.find(a => a.automaton_id === pendingId);
          if (match) {
            setCurrentAutomaton(match);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch automata:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchAutomata();
      fetchGroups();
    } else {
      // Guest mode: initialize with a local scratch pad in a default group
      const guestGroupId = 'guest_group_default';
      setGroups([{ group_id: guestGroupId, name: 'My Automata', order: 0 }]);
      const scratchPad = {
        automaton_id: `local_${Date.now()}`,
        name: 'Scratch Pad',
        type: 'DFA',
        states: [],
        alphabet: [],
        transitions: [],
        language_description: '',
        group_id: guestGroupId,
      };
      setAutomata([scratchPad]);
      setCurrentAutomaton(scratchPad);
    }
  }, [user, fetchAutomata]);

  // Create new automaton
  const createAutomaton = useCallback(async (name = 'Untitled Automaton', type = 'DFA', groupId = null) => {
    const typeFields = type === 'PDA'
      ? { initial_stack_symbol: 'Z', acceptance_mode: 'final_state' }
      : type === 'TM'
      ? { blank_symbol: '\u25A1', step_limit: 10000 }
      : {};
    if (isGuest) {
      // Guest mode: create locally
      const localAutomaton = {
        automaton_id: `local_${Date.now()}`,
        name,
        type,
        states: [],
        alphabet: [],
        transitions: [],
        group_id: groupId || groups[0]?.group_id || null,
        ...typeFields,
      };
      setAutomata(prev => [...prev, localAutomaton]);
      setCurrentAutomaton(localAutomaton);
      toast.success('New automaton created');
      return localAutomaton;
    }

    try {
      const { ok, data: newAutomaton } = await api.post('/automata', {
        name, type, states: [], alphabet: [], transitions: [],
        group_id: groupId,
        ...typeFields,
      });
      if (!ok) throw new Error('Failed to create automaton');

      setAutomata(prev => [...prev, newAutomaton]);
      setCurrentAutomaton(newAutomaton);
      toast.success('New automaton created');
      return newAutomaton;
    } catch (error) {
      toast.error('Failed to create automaton');
      throw error;
    }
  }, [isGuest, groups]);

  // Load specific automaton
  const loadAutomaton = useCallback(async (automatonId) => {
    if (isGuest) {
      const local = automata.find(a => a.automaton_id === automatonId);
      if (local) setCurrentAutomaton(local);
      return local;
    }

    try {
      const { ok, data: automaton } = await api.get(`/automata/${automatonId}`);
      if (!ok) throw new Error('Failed to load automaton');

      setCurrentAutomaton(automaton);
      setAutomata(prev => prev.map(a => a.automaton_id === automatonId ? { ...a, last_opened_at: automaton.last_opened_at } : a));
      return automaton;
    } catch (error) {
      toast.error('Failed to load automaton');
      throw error;
    }
  }, [isGuest, automata]);

  // Update current automaton (triggers auto-save)
  const updateCurrentAutomaton = useCallback((updates) => {
    setCurrentAutomaton(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      scheduleAutoSave(updated);
      // Keep automata list in sync so switching back preserves changes (especially for guests)
      setAutomata(list => list.map(a => a.automaton_id === updated.automaton_id ? { ...a, ...updates } : a));
      return updated;
    });
  }, [scheduleAutoSave]);

  // Delete automaton
  const deleteAutomaton = useCallback(async (automatonId) => {
    if (isGuest) {
      setAutomata(prev => prev.filter(a => a.automaton_id !== automatonId));
      if (currentAutomaton?.automaton_id === automatonId) {
        setCurrentAutomaton(null);
      }
      toast.success('Automaton deleted');
      return;
    }

    try {
      const { ok } = await api.delete(`/automata/${automatonId}`);
      if (!ok) throw new Error('Failed to delete automaton');

      setAutomata(prev => prev.filter(a => a.automaton_id !== automatonId));
      if (currentAutomaton?.automaton_id === automatonId) {
        setCurrentAutomaton(null);
      }
      toast.success('Automaton deleted');
    } catch (error) {
      toast.error('Failed to delete automaton');
      throw error;
    }
  }, [currentAutomaton, isGuest]);

  // Rename automaton
  const renameAutomaton = useCallback(async (automatonId, newName) => {
    if (isGuest) {
      setAutomata(prev => prev.map(a => a.automaton_id === automatonId ? { ...a, name: newName } : a));
      if (currentAutomaton?.automaton_id === automatonId) {
        setCurrentAutomaton(prev => ({ ...prev, name: newName }));
      }
      return;
    }

    try {
      const { ok, data: updated } = await api.put(`/automata/${automatonId}`, { name: newName });
      if (!ok) throw new Error('Failed to rename automaton');

      setAutomata(prev => prev.map(a => a.automaton_id === automatonId ? updated : a));
      if (currentAutomaton?.automaton_id === automatonId) {
        setCurrentAutomaton(updated);
      }
    } catch (error) {
      toast.error('Failed to rename automaton');
      throw error;
    }
  }, [currentAutomaton, isGuest]);

  // Duplicate automaton
  const duplicateAutomaton = useCallback(async (automatonId) => {
    const original = automata.find(a => a.automaton_id === automatonId);
    if (!original) {
      toast.error('Automaton not found');
      return;
    }

    if (isGuest) {
      const duplicate = {
        automaton_id: `local_${Date.now()}`,
        name: `${original.name} (Copy)`,
        type: original.type,
        states: original.states,
        alphabet: original.alphabet,
        transitions: original.transitions,
        group_id: original.group_id,
      };
      setAutomata(prev => [...prev, duplicate]);
      toast.success('Automaton duplicated');
      return duplicate;
    }

    try {
      const { ok, data: duplicated } = await api.post('/automata', {
        name: `${original.name} (Copy)`,
        type: original.type,
        states: original.states,
        alphabet: original.alphabet,
        transitions: original.transitions,
        group_id: original.group_id,
      });
      if (!ok) throw new Error('Failed to duplicate automaton');

      setAutomata(prev => [...prev, duplicated]);
      toast.success('Automaton duplicated');
      return duplicated;
    } catch (error) {
      toast.error('Failed to duplicate automaton');
      throw error;
    }
  }, [automata, isGuest]);

  // Toggle pinned/favorite state on an automaton
  const togglePinAutomaton = useCallback(async (automatonId) => {
    const target = automata.find(a => a.automaton_id === automatonId);
    if (!target) return;
    const newPinned = !target.pinned;

    setAutomata(prev => prev.map(a => a.automaton_id === automatonId ? { ...a, pinned: newPinned } : a));
    if (currentAutomaton?.automaton_id === automatonId) {
      setCurrentAutomaton(prev => ({ ...prev, pinned: newPinned }));
    }

    if (isGuest) return;

    try {
      const { ok } = await api.put(`/automata/${automatonId}`, { pinned: newPinned });
      if (!ok) throw new Error('Failed to update pin');
    } catch (error) {
      // Revert on failure
      setAutomata(prev => prev.map(a => a.automaton_id === automatonId ? { ...a, pinned: !newPinned } : a));
      if (currentAutomaton?.automaton_id === automatonId) {
        setCurrentAutomaton(prev => ({ ...prev, pinned: !newPinned }));
      }
      toast.error('Failed to update pin');
    }
  }, [automata, currentAutomaton, isGuest]);

  // ============== UNDO / REDO ==============
  const undoStacks = useRef({}); // { [automatonId]: { undo: [], redo: [] } }
  const [undoVersion, setUndoVersion] = useState(0);
  const lastCaptureTime = useRef(0);
  const currentAutomatonRef = useRef(currentAutomaton);
  useEffect(() => { currentAutomatonRef.current = currentAutomaton; }, [currentAutomaton]);

  const MAX_UNDO = 50;
  const DEBOUNCE_MS = 300;

  const getStack = useCallback((id) => {
    if (!undoStacks.current[id]) undoStacks.current[id] = { undo: [], redo: [] };
    return undoStacks.current[id];
  }, []);

  const takeSnapshot = useCallback((automaton) => ({
    states: JSON.parse(JSON.stringify(automaton.states || [])),
    transitions: JSON.parse(JSON.stringify(automaton.transitions || [])),
    alphabet: [...(automaton.alphabet || [])],
  }), []);

  const captureSnapshot = useCallback(() => {
    const automaton = currentAutomatonRef.current;
    if (!automaton) return;
    const now = Date.now();
    if (now - lastCaptureTime.current < DEBOUNCE_MS) return; // coalesce rapid changes (e.g. drag)
    const stack = getStack(automaton.automaton_id);
    stack.undo.push(takeSnapshot(automaton));
    if (stack.undo.length > MAX_UNDO) stack.undo.shift();
    stack.redo = [];
    lastCaptureTime.current = now;
    setUndoVersion(v => v + 1);
  }, [getStack, takeSnapshot]);

  const undo = useCallback(() => {
    const automaton = currentAutomatonRef.current;
    if (!automaton) return;
    const stack = getStack(automaton.automaton_id);
    if (stack.undo.length === 0) return;
    stack.redo.push(takeSnapshot(automaton));
    const prev = stack.undo.pop();
    setCurrentAutomaton(a => ({ ...a, states: prev.states, transitions: prev.transitions, alphabet: prev.alphabet }));
    setUndoVersion(v => v + 1);
  }, [getStack, takeSnapshot]);

  const redo = useCallback(() => {
    const automaton = currentAutomatonRef.current;
    if (!automaton) return;
    const stack = getStack(automaton.automaton_id);
    if (stack.redo.length === 0) return;
    stack.undo.push(takeSnapshot(automaton));
    const next = stack.redo.pop();
    setCurrentAutomaton(a => ({ ...a, states: next.states, transitions: next.transitions, alphabet: next.alphabet }));
    setUndoVersion(v => v + 1);
  }, [getStack, takeSnapshot]);

  const canUndo = currentAutomaton ? (undoStacks.current[currentAutomaton.automaton_id]?.undo?.length > 0) : false;
  const canRedo = currentAutomaton ? (undoStacks.current[currentAutomaton.automaton_id]?.redo?.length > 0) : false;
  // undoVersion used to trigger re-render for canUndo/canRedo
  void undoVersion;

  // Add state
  const addState = useCallback((x, y) => {
    captureSnapshot();
    const stateCount = currentAutomaton?.states?.length || 0;
    const newState = {
      id: generateStateId(),
      name: `q${stateCount}`,
      x,
      y,
      radius: 30,
      is_initial: stateCount === 0,
      is_final: false,
    };

    updateCurrentAutomaton({
      states: [...(currentAutomaton?.states || []), newState],
    });

    return newState;
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Update state
  const updateState = useCallback((stateId, updates) => {
    captureSnapshot();
    if (!currentAutomaton) return;

    let newStates = currentAutomaton.states.map(s => 
      s.id === stateId ? { ...s, ...updates } : s
    );

    // If setting as initial, clear other initial states
    if (updates.is_initial) {
      newStates = newStates.map(s => ({
        ...s,
        is_initial: s.id === stateId,
      }));
    }

    updateCurrentAutomaton({ states: newStates });
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Delete state
  const deleteState = useCallback((stateId) => {
    captureSnapshot();
    if (!currentAutomaton) return;

    const newStates = currentAutomaton.states.filter(s => s.id !== stateId);
    const newTransitions = currentAutomaton.transitions.filter(
      t => t.from_state !== stateId && t.to_state !== stateId
    );

    updateCurrentAutomaton({
      states: newStates,
      transitions: newTransitions,
    });
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Add transition
  const addTransition = useCallback((fromStateId, toStateId, symbols = [], pdaRules = null) => {
    captureSnapshot();
    if (!currentAutomaton) return null;

    const allTransitions = currentAutomaton.transitions || [];
    const allStates = currentAutomaton.states || [];
    const isPDA = currentAutomaton.type === 'PDA';

    const newTransition = {
      id: generateTransitionId(),
      from_state: fromStateId,
      to_state: toStateId,
      symbols: isPDA ? [] : symbols,
      curve: 0,
    };

    if (isPDA) {
      // Pre-fill pop with initial stack symbol when creating from the initial state
      const fromState = allStates.find(s => s.id === fromStateId);
      const prefillPop = fromState?.is_initial ? (currentAutomaton.initial_stack_symbol || 'Z') : '';
      newTransition.pda_rules = pdaRules || [{ input: '', pop: prefillPop, push: '' }];
    }

    if (currentAutomaton.type === 'TM') {
      // Initialize with empty TM rules for TM automata
      newTransition.tm_rules = [{ read: '', write: '', direction: 'R' }];
    }

    let updatedTransitions = [...allTransitions];

    if (fromStateId === toStateId) {
      // ── Self-loop: choose angle to avoid overlap ──
      const state = allStates.find(s => s.id === fromStateId);
      if (state) {
        const occupiedAngles = [];

        // Angles to connected states (edges from/to this state)
        allTransitions.forEach(t => {
          if (t.from_state === fromStateId && t.to_state !== fromStateId) {
            const other = allStates.find(s => s.id === t.to_state);
            if (other) occupiedAngles.push(Math.atan2(other.y - state.y, other.x - state.x));
          }
          if (t.to_state === fromStateId && t.from_state !== fromStateId) {
            const other = allStates.find(s => s.id === t.from_state);
            if (other) occupiedAngles.push(Math.atan2(other.y - state.y, other.x - state.x));
          }
        });

        // Angles of existing self-loops
        allTransitions.forEach(t => {
          if (t.from_state === fromStateId && t.to_state === fromStateId) {
            occupiedAngles.push(t.loopAngle !== undefined ? t.loopAngle : -Math.PI / 2);
          }
        });

        // Angles to nearby states (within 150 world units)
        allStates.forEach(s => {
          if (s.id === fromStateId) return;
          const dx = s.x - state.x, dy = s.y - state.y;
          if (Math.sqrt(dx * dx + dy * dy) < 150) {
            occupiedAngles.push(Math.atan2(dy, dx));
          }
        });

        // Initial state arrow comes from the left (angle = π)
        if (state.is_initial) {
          occupiedAngles.push(Math.PI);
        }

        if (occupiedAngles.length === 0) {
          newTransition.loopAngle = -Math.PI / 2; // default: top
        } else {
          // Normalize angles to [-π, π], sort, find largest gap
          const normalized = occupiedAngles.map(a => ((a + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI);
          normalized.sort((a, b) => a - b);
          let bestGap = 0, bestAngle = -Math.PI / 2;
          for (let i = 0; i < normalized.length; i++) {
            const next = i + 1 < normalized.length ? normalized[i + 1] : normalized[0] + 2 * Math.PI;
            const gap = next - normalized[i];
            if (gap > bestGap) {
              bestGap = gap;
              bestAngle = normalized[i] + gap / 2;
            }
          }
          // Normalize result to [-π, π]
          newTransition.loopAngle = ((bestAngle + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
        }
      }
    } else {
      // ── Regular edge: auto-curve for parallel and bidirectional edges ──
      const CURVE_AMOUNT = 0.9;
      const sameDir = updatedTransitions.filter(
        t => t.from_state === fromStateId && t.to_state === toStateId
      );
      const reverseEdge = updatedTransitions.find(
        t => t.from_state === toStateId && t.to_state === fromStateId
      );

      if (sameDir.length > 0) {
        // Same-direction parallel edges exist — spread apart
        if (sameDir.length === 1 && Math.abs(sameDir[0].curve || 0) < 0.05) {
          // Only one existing edge at curve ≈ 0 → push the pair apart
          updatedTransitions = updatedTransitions.map(t =>
            t.id === sameDir[0].id ? { ...t, curve: CURVE_AMOUNT } : t
          );
          newTransition.curve = -CURVE_AMOUNT;
        } else {
          // Place new edge further out on the less-crowded side
          const maxAbs = Math.max(...sameDir.map(t => Math.abs(t.curve || 0)));
          const nextMag = maxAbs + CURVE_AMOUNT;
          const posCount = sameDir.filter(t => (t.curve || 0) > 0).length;
          const negCount = sameDir.filter(t => (t.curve || 0) <= 0).length;
          newTransition.curve = posCount <= negCount ? nextMag : -nextMag;
        }
      } else if (reverseEdge) {
        // Only reverse edge(s) — bidirectional auto-curve
        const existingCurve = reverseEdge.curve || 0;

        if (existingCurve === 0) {
          updatedTransitions = updatedTransitions.map(t =>
            t.id === reverseEdge.id ? { ...t, curve: CURVE_AMOUNT } : t
          );
          newTransition.curve = CURVE_AMOUNT;
        } else {
          newTransition.curve = existingCurve;
        }
      }
    }

    updatedTransitions.push(newTransition);

    // Derive alphabet: for PDA, extract input symbols from pda_rules
    let newAlphabet;
    if (isPDA) {
      const allInputs = updatedTransitions.flatMap(t =>
        (t.pda_rules || []).map(r => r.input).filter(s => s && s !== 'λ' && s !== 'ε')
      );
      newAlphabet = [...new Set([...(currentAutomaton.alphabet || []), ...allInputs])];
    } else {
      newAlphabet = [...new Set([...(currentAutomaton.alphabet || []), ...symbols])];
    }

    updateCurrentAutomaton({
      transitions: updatedTransitions,
      alphabet: newAlphabet,
    });

    return newTransition;
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Update transition
  const updateTransition = useCallback((transitionId, updates) => {
    captureSnapshot();
    if (!currentAutomaton) return;
    const isPDA = currentAutomaton.type === 'PDA';

    const newTransitions = currentAutomaton.transitions.map(t =>
      t.id === transitionId ? { ...t, ...updates } : t
    );

    // Update alphabet
    let newAlphabet = currentAutomaton.alphabet;
    if (isPDA && updates.pda_rules) {
      const allInputs = newTransitions.flatMap(t =>
        (t.pda_rules || []).map(r => r.input).filter(s => s && s !== 'λ' && s !== 'ε')
      );
      newAlphabet = [...new Set(allInputs)];
    } else if (updates.symbols) {
      const allSymbols = newTransitions.flatMap(t => t.symbols);
      newAlphabet = [...new Set(allSymbols)];
    }

    updateCurrentAutomaton({
      transitions: newTransitions,
      alphabet: newAlphabet,
    });
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Delete transition
  const deleteTransition = useCallback((transitionId) => {
    captureSnapshot();
    if (!currentAutomaton) return;
    const isPDA = currentAutomaton.type === 'PDA';

    const newTransitions = currentAutomaton.transitions.filter(t => t.id !== transitionId);
    
    // Recalculate alphabet
    let newAlphabet;
    if (isPDA) {
      const allInputs = newTransitions.flatMap(t =>
        (t.pda_rules || []).map(r => r.input).filter(s => s && s !== 'λ' && s !== 'ε')
      );
      newAlphabet = [...new Set(allInputs)];
    } else {
      const allSymbols = newTransitions.flatMap(t => t.symbols);
      newAlphabet = [...new Set(allSymbols)];
    }

    updateCurrentAutomaton({
      transitions: newTransitions,
      alphabet: newAlphabet,
    });
  }, [currentAutomaton, updateCurrentAutomaton]);

  // Export automaton
  const exportAutomaton = useCallback(async () => {
    if (!currentAutomaton) return null;

    const exportData = {
      name: currentAutomaton.name,
      type: currentAutomaton.type,
      states: currentAutomaton.states,
      alphabet: currentAutomaton.alphabet,
      transitions: currentAutomaton.transitions,
      language_description: currentAutomaton.language_description || '',
      test_accept_strings: currentAutomaton.test_accept_strings || [],
      test_reject_strings: currentAutomaton.test_reject_strings || [],
    };
    if (currentAutomaton.type === 'PDA') {
      exportData.initial_stack_symbol = currentAutomaton.initial_stack_symbol || 'Z';
      exportData.acceptance_mode = currentAutomaton.acceptance_mode || 'final_state';
    }
    if (currentAutomaton.type === 'TM') {
      exportData.blank_symbol = currentAutomaton.blank_symbol || '\u25A1';
      exportData.step_limit = currentAutomaton.step_limit || 10000;
    }

    return exportData;
  }, [currentAutomaton]);

  // Import automaton
  const importAutomaton = useCallback(async (data) => {
    if (isGuest) {
      const pdaFields = data.type === 'PDA' ? {
        initial_stack_symbol: data.initial_stack_symbol || 'Z',
        acceptance_mode: data.acceptance_mode || 'final_state',
      } : {};
      const tmFields = data.type === 'TM' ? {
        blank_symbol: data.blank_symbol || '\u25A1',
        step_limit: data.step_limit || 10000,
      } : {};
      const imported = {
        automaton_id: `local_${Date.now()}`,
        name: data.name || 'Imported Automaton',
        type: data.type || 'DFA',
        states: data.states || [],
        alphabet: data.alphabet || [],
        transitions: data.transitions || [],
        language_description: data.language_description || '',
        test_accept_strings: data.test_accept_strings || [],
        test_reject_strings: data.test_reject_strings || [],
        ...pdaFields,
        ...tmFields,
      };
      setAutomata(prev => [...prev, imported]);
      setCurrentAutomaton(imported);
      toast.success('Automaton imported');
      return imported;
    }

    try {
      const { ok, data: imported } = await api.post('/automata/import', data);
      if (!ok) throw new Error('Failed to import automaton');

      setAutomata(prev => [...prev, imported]);
      setCurrentAutomaton(imported);
      toast.success('Automaton imported successfully');
      return imported;
    } catch (error) {
      toast.error('Failed to import automaton');
      throw error;
    }
  }, [isGuest]);

  // Verify automaton: check DFA vs NFA criteria
  const verifyAutomaton = useCallback((mode = 'strict') => {
    if (!currentAutomaton) return null;

    const { states, transitions, type } = currentAutomaton;
    const issues = [];
    const epsilonSymbols = new Set(['ε', 'λ']);

    if (states.length === 0) {
      return { is_dfa: false, classification: type === 'PDA' ? 'PDA' : 'Undefined', issues: [{ type: 'error', message: 'No states defined' }] };
    }

    // Check initial state
    const initialStates = states.filter(s => s.is_initial);
    if (initialStates.length === 0) {
      issues.push({ type: 'error', message: 'No initial state defined' });
    } else if (initialStates.length > 1) {
      issues.push({ type: 'error', message: `Multiple initial states: ${initialStates.map(s => s.name).join(', ')}` });
    }

    if (type === 'PDA') {
      // PDA-specific verification
      const z0 = currentAutomaton.initial_stack_symbol;
      if (!z0) {
        issues.push({ type: 'warning', message: 'No initial stack symbol defined' });
      }

      // Check if all stack symbols referenced in transitions are consistent
      const stackSymbols = new Set();
      if (z0) stackSymbols.add(z0);
      for (const t of transitions) {
        for (const r of (t.pda_rules || [])) {
          if (r.pop && !epsilonSymbols.has(r.pop)) stackSymbols.add(r.pop);
          if (r.push) {
            for (const ch of r.push) {
              if (!epsilonSymbols.has(ch) && ch !== '\u03BB') stackSymbols.add(ch);
            }
          }
        }
      }

      // Check for transitions with empty rules
      for (const t of transitions) {
        if (!t.pda_rules || t.pda_rules.length === 0) {
          const from = states.find(s => s.id === t.from_state)?.name || '?';
          const to = states.find(s => s.id === t.to_state)?.name || '?';
          issues.push({ type: 'warning', message: `Transition ${from} → ${to} has no rules` });
        }
      }

      // Check for unreachable states
      if (initialStates.length > 0) {
        const reachable = new Set();
        const queue = [initialStates[0].id];
        reachable.add(initialStates[0].id);
        while (queue.length > 0) {
          const sid = queue.shift();
          for (const t of transitions) {
            if (t.from_state === sid && !reachable.has(t.to_state)) {
              reachable.add(t.to_state);
              queue.push(t.to_state);
            }
          }
        }
        const unreachable = states.filter(s => !reachable.has(s.id));
        if (unreachable.length > 0) {
          issues.push({ type: 'info', message: `Unreachable states: ${unreachable.map(s => s.name).join(', ')}` });
        }
      }

      if (currentAutomaton.acceptance_mode === 'final_state' && !states.some(s => s.is_final)) {
        issues.push({ type: 'info', message: 'Acceptance mode is "Final State" but no final states defined' });
      }

      return { is_dfa: false, classification: 'PDA', issues };
    }

    // DFA/NFA verification (unchanged)
    const realAlphabet = new Set();
    let hasEpsilon = false;
    for (const t of transitions) {
      for (const sym of t.symbols) {
        if (epsilonSymbols.has(sym)) {
          hasEpsilon = true;
        } else {
          realAlphabet.add(sym);
        }
      }
    }

    if (hasEpsilon) {
      issues.push({ type: 'info', message: 'Contains ε/λ transitions (epsilon moves) — makes this an NFA' });
    }

    for (const state of states) {
      const symbolDests = {};
      for (const t of transitions) {
        if (t.from_state === state.id) {
          for (const sym of t.symbols) {
            if (epsilonSymbols.has(sym)) continue;
            if (!symbolDests[sym]) symbolDests[sym] = new Set();
            symbolDests[sym].add(t.to_state);
          }
        }
      }

      for (const [sym, destSet] of Object.entries(symbolDests)) {
        if (destSet.size > 1) {
          const destNames = [...destSet].map(id => states.find(s => s.id === id)?.name || '?');
          issues.push({
            type: 'error',
            message: `State '${state.name}': multiple transitions for '${sym}' → {${destNames.join(', ')}} (nondeterministic)`
          });
        }
      }
    }

    if (mode === 'strict' && realAlphabet.size > 0) {
      for (const state of states) {
        const stateSymbols = new Set();
        for (const t of transitions) {
          if (t.from_state === state.id) {
            for (const sym of t.symbols) {
              if (!epsilonSymbols.has(sym)) stateSymbols.add(sym);
            }
          }
        }

        const missing = [];
        for (const sym of realAlphabet) {
          if (!stateSymbols.has(sym)) missing.push(sym);
        }
        if (missing.length > 0) {
          issues.push({
            type: 'warning',
            message: `State '${state.name}': no transition for {${missing.join(', ')}}`
          });
        }
      }
    }

    if (!states.some(s => s.is_final)) {
      issues.push({ type: 'info', message: 'No accepting (final) states defined' });
    }

    const hasErrors = issues.some(i => i.type === 'error');
    const hasWarnings = issues.some(i => i.type === 'warning');
    const is_dfa = !hasErrors && !hasEpsilon && !hasWarnings;

    return {
      is_dfa,
      classification: is_dfa ? 'DFA' : 'NFA',
      issues
    };
  }, [currentAutomaton]);

  // ============== GROUP MANAGEMENT ==============

  const fetchGroups = useCallback(async () => {
    if (!user) return;
    try {
      const { ok, data } = await api.get('/groups');
      if (ok) setGroups(data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  }, [user]);

  const createGroup = useCallback(async (name) => {
    if (isGuest) {
      const g = { group_id: `guest_group_${Date.now()}`, name, order: groups.length };
      setGroups(prev => [...prev, g]);
      toast.success('Group created');
      return g;
    }
    try {
      const { ok, data } = await api.post('/groups', { name });
      if (!ok) throw new Error();
      setGroups(prev => [...prev, data]);
      toast.success('Group created');
      return data;
    } catch {
      toast.error('Failed to create group');
    }
  }, [isGuest, groups.length]);

  const renameGroup = useCallback(async (groupId, name) => {
    if (isGuest) {
      setGroups(prev => prev.map(g => g.group_id === groupId ? { ...g, name } : g));
      return;
    }
    try {
      await api.patch(`/groups/${groupId}`, { name });
      setGroups(prev => prev.map(g => g.group_id === groupId ? { ...g, name } : g));
    } catch {
      toast.error('Failed to rename group');
    }
  }, [isGuest]);

  const deleteGroup = useCallback(async (groupId) => {
    if (groups.length <= 1) {
      toast.error('Cannot delete the last group');
      return;
    }
    if (isGuest) {
      const other = groups.find(g => g.group_id !== groupId);
      setAutomata(prev => prev.map(a => a.group_id === groupId ? { ...a, group_id: other.group_id } : a));
      setGroups(prev => prev.filter(g => g.group_id !== groupId));
      return;
    }
    try {
      await api.delete(`/groups/${groupId}`);
      await fetchGroups();
      await fetchAutomata();
    } catch {
      toast.error('Failed to delete group');
    }
  }, [isGuest, groups, fetchGroups, fetchAutomata]);

  const reorderGroups = useCallback(async (orderedIds) => {
    const reordered = orderedIds.map((id, i) => {
      const g = groups.find(g => g.group_id === id);
      return g ? { ...g, order: i } : null;
    }).filter(Boolean);
    setGroups(reordered);
    if (!isGuest) {
      try { await api.post('/groups/reorder', { group_ids: orderedIds }); } catch {}
    }
  }, [groups, isGuest]);

  const moveAutomaton = useCallback(async (automatonId, newGroupId, targetIndex) => {
    setAutomata(prev => {
      const updated = prev.map(a =>
        a.automaton_id === automatonId ? { ...a, group_id: newGroupId } : a
      );
      // Reorder within the target group
      const inGroup = updated
        .filter(a => a.group_id === newGroupId || (!a.group_id && groups.find(g => g.group_id === newGroupId)?.order === 0))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const movedItem = inGroup.find(a => a.automaton_id === automatonId);
      const rest = inGroup.filter(a => a.automaton_id !== automatonId);
      const idx = targetIndex != null ? Math.min(targetIndex, rest.length) : rest.length;
      rest.splice(idx, 0, movedItem);
      const orderMap = {};
      rest.forEach((a, i) => { orderMap[a.automaton_id] = i; });
      return updated.map(a => orderMap[a.automaton_id] != null ? { ...a, order: orderMap[a.automaton_id] } : a);
    });
    if (currentAutomaton?.automaton_id === automatonId) {
      setCurrentAutomaton(prev => prev ? { ...prev, group_id: newGroupId } : prev);
    }
    if (!isGuest) {
      try { await api.put(`/automata/${automatonId}`, { group_id: newGroupId }); } catch {}
      // Persist new order
      const allAut = automata.map(a => a.automaton_id === automatonId ? { ...a, group_id: newGroupId } : a);
      const inGroup = allAut
        .filter(a => a.group_id === newGroupId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const movedItem = inGroup.find(a => a.automaton_id === automatonId);
      const rest = inGroup.filter(a => a.automaton_id !== automatonId);
      const idx = targetIndex != null ? Math.min(targetIndex, rest.length) : rest.length;
      rest.splice(idx, 0, movedItem);
      try { await api.post('/automata/reorder', { group_id: newGroupId, automaton_ids: rest.map(a => a.automaton_id) }); } catch {}
    }
  }, [isGuest, currentAutomaton, automata, groups]);

  const reorderAutomata = useCallback(async (groupId, orderedIds) => {
    setAutomata(prev => {
      const orderMap = {};
      orderedIds.forEach((id, i) => { orderMap[id] = i; });
      return prev.map(a => orderMap[a.automaton_id] != null ? { ...a, order: orderMap[a.automaton_id] } : a);
    });
    if (!isGuest) {
      try { await api.post('/automata/reorder', { group_id: groupId, automaton_ids: orderedIds }); } catch {}
    }
  }, [isGuest]);

  const value = {
    automata,
    currentAutomaton,
    loading,
    saving,
    isGuest,
    groups,
    fetchAutomata,
    fetchGroups,
    createAutomaton,
    loadAutomaton,
    updateCurrentAutomaton,
    deleteAutomaton,
    renameAutomaton,
    duplicateAutomaton,
    togglePinAutomaton,
    addState,
    updateState,
    deleteState,
    addTransition,
    updateTransition,
    deleteTransition,
    exportAutomaton,
    importAutomaton,
    verifyAutomaton,
    undo,
    redo,
    canUndo,
    canRedo,
    createGroup,
    renameGroup,
    deleteGroup,
    reorderGroups,
    moveAutomaton,
    reorderAutomata,
  };

  return (
    <AutomataContext.Provider value={value}>
      {children}
    </AutomataContext.Provider>
  );
};
